import type { V2 } from '../../core/math';
import type { ZoneOpts } from '../../game/netHooks';
import type { Actor } from '../../sim/actor';
import type { Team } from '../../sim/types';
import { SPARRER_ID } from './content';
import { type DeathView, type HudView, type InstanceEnv, type ResultView, emptyHud, once } from './env';
import { addItems, emptyDrop } from './rewards';
import { CRUCIBLE } from './rules';
import { InstanceScript, type ScriptState, str } from './script';

/**
 * THE CRUCIBLE (D19) — 3v3 elimination, best of five (research 01 §8: Last
 * Team Standing). Works for 1v1 / 2v2 and uneven teams. Teams come from
 * matchmaking (`opts.team`, snake-drafted by item level); a party entering
 * together is split by the host. A side without human players is filled with
 * sparring knights so a solo or one-sided group can practise (reduced
 * rewards). PvP stat normalisation and CC safety nets live in the engine
 * (`world.pvp`, `build({ pvp: true })`, debuff immunity after hard CC).
 *
 * The host is authoritative for teams, rounds and the score; player deaths
 * replicate. At each round start every peer moves its own character to its
 * team's spawn with full HP, reset cooldowns and a short invulnerable,
 * rooted countdown.
 */

export type Side = 'red' | 'blue';
export type CruciblePhase = 'warmup' | 'countdown' | 'fight' | 'roundEnd' | 'done';

export interface CrucibleState extends ScriptState {
  phase: CruciblePhase;
  /** Current round (0 during warm-up). */
  round: number;
  red: number;
  blue: number;
  /** Seconds left in the current phase. */
  t: number;
  /** Player (owner peer) → side. */
  teams: Record<string, Side>;
  /** Side filled with sparring knights (practice), if any. */
  bots: Side | null;
  lastWinner: Side | 'draw' | null;
  winner: Side | 'draw' | null;
}

const isSide = (v: unknown): v is Side => v === 'red' || v === 'blue';
const other = (s: Side): Side => (s === 'red' ? 'blue' : 'red');
const SIDE_NAME: Record<Side, string> = { red: 'Crimson', blue: 'Azure' };

/** Splits players into two sides: keeps matchmade teams, balances everyone else by id. */
export function assignSides(players: { owner: string; team: Team }[], current: Record<string, Side> = {}): Record<string, Side> {
  const out: Record<string, Side> = {};
  const loose: string[] = [];
  for (const p of players) {
    if (isSide(current[p.owner])) out[p.owner] = current[p.owner] as Side;
    else if (isSide(p.team)) out[p.owner] = p.team;
    else loose.push(p.owner);
  }
  for (const owner of loose.sort()) {
    const red = Object.values(out).filter((s) => s === 'red').length;
    const blue = Object.values(out).filter((s) => s === 'blue').length;
    out[owner] = red <= blue ? 'red' : 'blue';
  }
  return out;
}

/** Round winner from the living members of each side (null while both stand). */
export function roundWinner(aliveRed: number, aliveBlue: number): Side | 'draw' | null {
  if (aliveRed <= 0 && aliveBlue <= 0) return 'draw';
  if (aliveRed <= 0) return 'blue';
  if (aliveBlue <= 0) return 'red';
  return null;
}

export class CrucibleScript extends InstanceScript<CrucibleState> {
  constructor(env: InstanceEnv, opts: ZoneOpts) {
    super(env, opts, { seq: 0, phase: 'warmup', round: 0, red: 0, blue: 0, t: CRUCIBLE.warmup, teams: {}, bots: null, lastWinner: null, winner: null });
  }

  /** The local player's side. */
  get mine(): Side | null {
    const s = this.state.teams[this.world.localPeer];
    if (isSide(s)) return s;
    const t = str(this.opts.team);
    return isSide(t) ? t : null;
  }

  get pvp(): boolean {
    return this.state.bots === null;
  }

  // ---------------------------------------------------------------- host

  protected override hostTick(dt: number): void {
    const s = this.state;
    if (s.phase === 'done') return;
    s.t -= dt;
    switch (s.phase) {
      case 'warmup':
        if (this.players().length >= Math.max(1, this.world.partySize) || s.t <= 0) this.startRound();
        break;
      case 'countdown':
        if (s.t <= 0) this.update({ phase: 'fight', t: CRUCIBLE.roundTime });
        break;
      case 'fight': {
        const alive = this.aliveBySide();
        const w = roundWinner(alive.red, alive.blue) ?? (s.t <= 0 ? this.hpDecision() : null);
        if (w) this.endRound(w);
        break;
      }
      case 'roundEnd':
        if (s.t <= 0) {
          if (s.red >= CRUCIBLE.roundsToWin || s.blue >= CRUCIBLE.roundsToWin || s.round >= CRUCIBLE.maxRounds) this.finishMatch();
          else this.startRound();
        }
        break;
      default:
        break;
    }
  }

  protected override mirrorTick(dt: number): void {
    if (this.state.phase !== 'done') this.state.t = Math.max(0, this.state.t - dt);
  }

  /** Members of a side: its players plus its sparring knights. */
  members(side: Side): Actor[] {
    const out: Actor[] = [];
    for (const a of this.world.actors.values()) {
      if (a.kind === 'player' && this.state.teams[a.owner] === side) out.push(a);
      else if (a.monster?.tags.includes('crucible:bot') && a.team === side) out.push(a);
    }
    return out;
  }

  aliveBySide(): Record<Side, number> {
    return { red: this.members('red').filter((a) => a.alive).length, blue: this.members('blue').filter((a) => a.alive).length };
  }

  /** Time ran out: the side with more total health left takes the round. */
  private hpDecision(): Side | 'draw' {
    const hp = (side: Side) => this.members(side).reduce((n, a) => n + (a.alive ? a.hpPct : 0), 0);
    const r = hp('red');
    const b = hp('blue');
    return Math.abs(r - b) < 1e-3 ? 'draw' : r > b ? 'red' : 'blue';
  }

  startRound(): void {
    const s = this.state;
    const teams = assignSides(this.players(), s.teams);
    const humans = (side: Side) => Object.values(teams).filter((x) => x === side).length;
    const bots: Side | null = humans('red') === 0 ? 'red' : humans('blue') === 0 ? 'blue' : null;
    this.despawnTagged('crucible:bot');
    if (bots) {
      const n = Math.min(3, Math.max(1, humans(other(bots))));
      for (let i = 0; i < n; i++) {
        const at = this.spawnPoint(`${bots}_${i}`);
        const knight = this.z.spawnMonster(SPARRER_ID, 30, { x: at.x, z: at.z }, { team: bots, facing: at.facing ?? 0, tags: ['crucible', 'crucible:bot'] });
        this.world.invuln(knight, CRUCIBLE.countdown);
        this.world.applyStatusTo(knight, { id: 'root', duration: CRUCIBLE.countdown }, knight.id);
      }
    }
    this.update({ phase: 'countdown', round: s.round + 1, t: CRUCIBLE.countdown, teams, bots, lastWinner: null });
  }

  private endRound(w: Side | 'draw'): void {
    const s = this.state;
    this.update({ phase: 'roundEnd', t: CRUCIBLE.roundEnd, lastWinner: w, red: s.red + (w === 'red' ? 1 : 0), blue: s.blue + (w === 'blue' ? 1 : 0) });
  }

  private finishMatch(): void {
    const s = this.state;
    const winner: Side | 'draw' = s.red === s.blue ? 'draw' : s.red > s.blue ? 'red' : 'blue';
    this.despawnTagged('crucible:bot');
    this.update({ phase: 'done', winner, t: 0 });
  }

  // ---------------------------------------------------------------- every peer

  protected override changed(prev: CrucibleState, next: CrucibleState): void {
    const me = this.me;
    const side = this.mine;
    if (me && side && me.team !== side) me.team = side;
    if (next.phase === prev.phase && next.round === prev.round) return;
    switch (next.phase) {
      case 'countdown':
        this.enterRound(next);
        break;
      case 'fight':
        this.env.announce('Fight!', 'boss');
        break;
      case 'roundEnd': {
        const w = next.lastWinner;
        this.env.announce(w === 'draw' || !w ? `Round ${next.round} is a draw` : `${SIDE_NAME[w]} takes round ${next.round}${w === side ? '!' : ''}`, w === side ? 'success' : w === 'draw' ? 'info' : 'warning');
        break;
      }
      case 'done':
        if (me && !me.alive) this.reviveMe(1);
        this.reward(next);
        break;
      default:
        break;
    }
  }

  /** Round start: back to my team's spawn, fully restored, invulnerable and rooted until the fight. */
  private enterRound(s: CrucibleState): void {
    const me = this.me;
    const side = this.mine;
    if (!me || !side) return;
    me.team = side;
    const mates = Object.entries(s.teams)
      .filter(([, t]) => t === side)
      .map(([o]) => o)
      .sort();
    const idx = Math.max(0, mates.indexOf(this.world.localPeer)) % 3;
    const at: V2 & { facing?: number } = this.spawnPoint(`${side}_${idx}`);
    me.statuses = [];
    me.statsDirty = true;
    this.placeMe(at, at.facing);
    this.reviveMe(1);
    me.hp = me.stats.maxHp;
    me.mp = me.stats.maxMp;
    for (const id of [...me.cooldowns.keys()]) if (!id.startsWith('bi_')) me.cooldowns.delete(id);
    this.world.invuln(me, CRUCIBLE.countdown + 0.5);
    this.world.applyStatusTo(me, { id: 'root', duration: CRUCIBLE.countdown }, me.id);
  }

  private reward(s: CrucibleState): void {
    const p = this.env.progression;
    const side = this.mine;
    if (!p || !once(this.env, `${this.run}:reward`)) return;
    const result = s.winner === 'draw' || !side ? 'draw' : s.winner === side ? 'win' : 'loss';
    const pvp = s.bots === null;
    const crests = pvp ? CRUCIBLE.crests[result] : result === 'win' ? CRUCIBLE.crests.practiceWin : CRUCIBLE.crests.practiceLoss;
    const drop = emptyDrop();
    addItems(drop, 'arena_crest', crests);
    drop.silver = pvp ? CRUCIBLE.silver[result] : 0;
    p.grantDrop(drop, 'The Crucible');
    p.char.counters.crucibleMatches = (p.char.counters.crucibleMatches ?? 0) + 1;
    if (result === 'win') p.char.counters.crucibleWins = (p.char.counters.crucibleWins ?? 0) + 1;
    this.env.memo.set(`${this.run}:result`, { result, crests, silver: drop.silver });
    this.env.announce(result === 'win' ? 'Victory!' : result === 'loss' ? 'Defeat' : 'Draw', result === 'win' ? 'success' : result === 'loss' ? 'warning' : 'info');
    this.env.save();
  }

  // ---------------------------------------------------------------- views

  view(): HudView {
    const s = this.state;
    const v = emptyHud('crucible', 'The Crucible', this.pvp ? 'Elimination · best of five' : 'Practice · sparring knights');
    v.score = { red: s.red, blue: s.blue, round: Math.max(1, s.round), maxRounds: CRUCIBLE.maxRounds, mine: this.mine };
    if (s.phase === 'fight') v.timer = { label: 'Round', seconds: s.t, warn: s.t < 20 };
    if (s.phase === 'warmup') v.banner = 'Waiting for combatants…';
    else if (s.phase === 'countdown') v.banner = `Round ${s.round} · ${Math.max(1, Math.ceil(s.t))}`;
    if (!this.pvp) v.badges.push({ text: 'Practice', tone: 'grey' });
    const side = this.mine;
    if (side) v.badges.push({ text: `${SIDE_NAME[side]} team`, tone: side === 'red' ? 'red' : 'blue' });
    v.actions.push(s.phase === 'done' ? { id: 'leave', label: 'Leave', tone: 'gold' } : this.leaveAction('Leave the match? Your team fights on without you.'));
    return v;
  }

  override deathView(): DeathView | null {
    if (this.state.phase === 'done') return null;
    return {
      title: 'Eliminated',
      text: 'You return at the start of the next round. Watch your team hold the line.',
      countdown: null,
      actions: [this.leaveAction('Leave the match? Your team fights on without you.')],
    };
  }

  override resultView(): ResultView | null {
    const s = this.state;
    if (s.phase !== 'done') return null;
    const r = this.env.memo.get(`${this.run}:result`) as { result: 'win' | 'loss' | 'draw'; crests: number; silver: number } | undefined;
    const result = r?.result ?? 'draw';
    return {
      title: result === 'win' ? 'Victory' : result === 'loss' ? 'Defeat' : 'Draw',
      subtitle: `${SIDE_NAME.red} ${s.red} — ${s.blue} ${SIDE_NAME.blue}`,
      tone: result === 'win' ? 'win' : result === 'loss' ? 'loss' : 'neutral',
      lines: [`+${r?.crests ?? 0} Crucible Crests${r?.silver ? ` · +${r.silver.toLocaleString('en-US')} silver` : ''}`, this.pvp ? 'Ranked elimination · best of five' : 'Practice match against sparring knights'],
      actions: [{ id: 'leave', label: 'Leave the Crucible', tone: 'gold' }],
    };
  }

  override action(id: string): void {
    if (id === 'leave') this.env.leave();
  }
}
